import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { clearSession, readAccessToken, readRefreshToken } from '../session/tokens'
import { refreshSession } from '../session/refreshSession'
import { SESSION_EXPIRED, SessionExpiredError } from '../session/sessionEvents'

export function createAuthenticatedClient(baseURL: string): AxiosInstance {
  const client = axios.create({ baseURL, timeout: 30_000 })
  const expire = () => {
    clearSession()
    window.dispatchEvent(new Event(SESSION_EXPIRED))
    return new SessionExpiredError()
  }

  client.interceptors.request.use(async (config) => {
    try {
      const token = readAccessToken() ?? (readRefreshToken() ? await refreshSession() : null)
      if (token) config.headers.Authorization = `Bearer ${token}`
      return config
    } catch (error) {
      if (error instanceof SessionExpiredError) throw expire()
      throw error
    }
  })

  client.interceptors.response.use(undefined, async (error) => {
    const request = error.config as (InternalAxiosRequestConfig & { retried?: boolean }) | undefined
    if (error.response?.status !== 401 || !request) throw error
    if (request.retried) throw expire()
    request.retried = true
    try {
      const current = readAccessToken()
      const token = current && request.headers.Authorization !== `Bearer ${current}` ? current : await refreshSession()
      request.headers.Authorization = `Bearer ${token}`
      return client(request)
    } catch (reason) {
      if (reason instanceof SessionExpiredError) throw expire()
      throw reason
    }
  })
  return client
}
